import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { Icon } from '@components/icons';

const StyledProjectsSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;

  /* Using global .numbered-heading styles */

  .projects-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 40px;

    /* Center a lone card in the last row when project count is odd */
    > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      max-width: calc(50% - 20px);
      margin: 0 auto;
    }

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;

      > *:last-child:nth-child(odd) {
        max-width: 100%;
      }
    }
  }
`;

const StyledProjectCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
  box-shadow: 0 15px 35px -20px rgba(0, 0, 0, 0.7);
  transition: transform 0.12s cubic-bezier(0.645, 0.045, 0.355, 1),
    border-color 0.12s cubic-bezier(0.645, 0.045, 0.355, 1),
    background 0.12s cubic-bezier(0.645, 0.045, 0.355, 1),
    box-shadow 0.12s cubic-bezier(0.645, 0.045, 0.355, 1);

  .terminal-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 15px;
    padding: 10px 15px;
    background: rgba(255, 255, 255, 0.03);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .terminal-dots {
    display: flex;
    gap: 6px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;

    &.red {
      background-color: #ff5f56;
    }
    &.yellow {
      background-color: #ffbd2e;
    }
    &.green {
      background-color: #27c93f;
    }
  }

  .terminal-title {
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    opacity: 0.7;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    transform: translateY(-6px) scale(1.03) !important;
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 42, 42, 0.7);
    box-shadow: 0 20px 40px -10px rgba(255, 42, 42, 0.2);

    .project-image::after {
      opacity: 0;
    }

    .project-title {
      color: #ff2a2a;
      a {
        color: #ff2a2a;
      }
    }
  }

  .project-image {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    justify-content: center;
    align-items: center;

    .img {
      width: 100% !important;
      height: 100% !important;

      img {
        object-fit: scale-down !important;
        object-position: center !important;
      }

      transition: transform 0.12s cubic-bezier(0.645, 0.045, 0.355, 1);
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 42, 42, 0.5);
      z-index: 2;
      transition: opacity 0.12s ease;
    }
  }

  .project-content {
    padding: 25px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    z-index: 3;
  }

  .project-overline {
    margin: 5px 0 12px;
    color: #ff4a4a;
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    display: inline-block;
    background: rgba(255, 42, 42, 0.1);
    border: 1px solid rgba(255, 42, 42, 0.25);
    padding: 3px 10px;
    border-radius: 4px;
    width: fit-content;
  }

  .project-title {
    color: var(--lightest-slate);
    font-size: clamp(20px, 3vw, 24px);
    font-weight: 700;
    letter-spacing: -0.5px;
    margin: 0 0 20px 0;
    transition: all 0.12s cubic-bezier(0.645, 0.045, 0.355, 1);
    display: flex;
    align-items: center;

    a {
      transition: all 0.12s cubic-bezier(0.645, 0.045, 0.355, 1);
      &::after {
        display: none;
      }
    }
  }

  .project-description {
    color: var(--light-slate);
    font-size: var(--fz-sm);
    line-height: 1.5;
    margin-bottom: 15px;
    font-weight: 400;
    background: rgba(0, 0, 0, 0.4);
    padding: 15px;
    border-radius: 6px;
    border-left: 2px solid #ff2a2a;
    box-shadow: -6px 0 16px -3px rgba(255, 42, 42, 0.75);
    font-family: var(--font-mono);
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
    padding: 0;
    list-style: none;
    margin-top: 15px;

    li {
      color: var(--slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      padding: 4px 8px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      transition: all 0.12s cubic-bezier(0.645, 0.045, 0.355, 1);

      &:hover {
        background: #ff2a2a;
        color: #08080c !important;
        border-color: #ff2a2a;
        transform: scale(1.05);
        cursor: pointer;
      }
    }
  }

  .project-links {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-top: 10px;

    .link-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 10px 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      font-weight: 500;
      border-radius: 6px;
      text-decoration: none;
      transition: all 0.12s cubic-bezier(0.645, 0.045, 0.355, 1);
      cursor: pointer;
      position: relative;

      svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
      }
    }

    .github-btn {
      background: #ff2a2a;
      color: var(--white) !important;
      border: 1px solid #ff2a2a;
      box-shadow: 0 4px 14px -4px rgba(255, 42, 42, 0.5);

      @media (hover: hover) {
        &:hover {
          background: #ff4a4a;
          border-color: #ff4a4a;
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 8px 22px -4px rgba(255, 42, 42, 0.7);
          color: var(--white) !important;

          &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            height: 15px;
            background: transparent;
          }
        }
      }

      &:focus-visible,
      &:active,
      &.touch-active {
        background: #ff4a4a;
        border-color: #ff4a4a;
        transform: translateY(-5px) scale(1.05);
        box-shadow: 0 8px 22px -4px rgba(255, 42, 42, 0.7);
        color: var(--white) !important;

        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          height: 15px;
          background: transparent;
        }
      }
    }

    .site-btn {
      background: rgba(255, 255, 255, 0.03);
      color: #ff2a2a !important;
      border: 1px solid rgba(255, 42, 42, 0.5);

      @media (hover: hover) {
        &:hover {
          background: #ff2a2a;
          border-color: #ff2a2a;
          color: var(--white) !important;
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 8px 22px -4px rgba(255, 42, 42, 0.5);

          &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            height: 15px;
            background: transparent;
          }
        }
      }

      &:focus-visible,
      &:active,
      &.touch-active {
        background: #ff2a2a;
        border-color: #ff2a2a;
        color: var(--white) !important;
        transform: translateY(-5px) scale(1.05);
        box-shadow: 0 8px 22px -4px rgba(255, 42, 42, 0.5);

        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          height: 15px;
          background: transparent;
        }
      }
    }
  }
`;

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 800, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
              cta
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const titleRefs = useRef([]);
  const descRefs = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) =>
      sr.reveal(ref, {
        ...srConfig(i * 100),
        afterReveal: el => {
          el.style.transition = '';
          el.style.transitionDelay = '';
        },
      }),
    );
  }, [prefersReducedMotion]);

  useEffect(() => {
    const alignHeights = () => {
      const titles = titleRefs.current;
      const descs = descRefs.current;

      // Reset all heights to auto to measure natural content height
      titles.forEach(el => {
        if (el) {
          el.style.height = 'auto';
        }
      });
      descs.forEach(el => {
        if (el) {
          el.style.height = 'auto';
        }
      });

      // If stacked (single column on mobile/tablet <= 1024px), keep height auto
      if (window.innerWidth <= 1024) {
        return;
      }

      // Group in pairs (2 columns) and set to max height
      for (let i = 0; i < featuredProjects.length; i += 2) {
        // Align project titles (so upper borders of description boxes align perfectly)
        const title1 = titles[i];
        const title2 = titles[i + 1];
        if (title1 && title2) {
          const h1 = title1.offsetHeight;
          const h2 = title2.offsetHeight;
          const maxHeight = Math.max(h1, h2);
          title1.style.height = `${maxHeight}px`;
          title2.style.height = `${maxHeight}px`;
        }

        // Align project description cards using scrollHeight + sub-pixel safety buffer
        const desc1 = descs[i];
        const desc2 = descs[i + 1];
        if (desc1 && desc2) {
          const h1 = desc1.scrollHeight;
          const h2 = desc2.scrollHeight;
          // Adding a +4px safety buffer prevents high-DPI scaling and sub-pixel rounding wraps
          const maxHeight = Math.max(h1, h2) + 4;
          desc1.style.height = `${maxHeight}px`;
          desc2.style.height = `${maxHeight}px`;
        }
      }
    };

    // Calculate heights after component mounts
    alignHeights();

    // Recalculate once custom web fonts are fully loaded to prevent fallback reflow overflows
    if (typeof document !== 'undefined' && document.fonts) {
      document.fonts.ready.then(alignHeights);
    }

    // Run after short delays to ensure hydration / layout completes
    const timer1 = setTimeout(alignHeights, 100);
    const timer2 = setTimeout(alignHeights, 500);
    const timer3 = setTimeout(alignHeights, 1500);

    window.addEventListener('resize', alignHeights);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', alignHeights);
    };
  }, [featuredProjects]);

  return (
    <StyledProjectsSection id="projects">
      <h2 className="numbered-heading" ref={revealTitle}>
        Featured Work
      </h2>

      <div className="projects-grid">
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover, cta } = frontmatter;
            const image = getImage(cover);

            // Remove the duplicate title link at the beginning of the markdown description HTML
            const cleanHtml = html
              ? html.trim().replace(/^<p>\s*<a[^>]*>.*?<\/a>\s*<\/p>/i, '')
              : '';

            // Extract GitHub and Live Site links dynamically
            let githubUrl = null;
            let siteUrl = null;

            if (github && github.includes('github.com')) {
              githubUrl = github;
            } else if (external && external.includes('github.com')) {
              githubUrl = external;
            } else if (cta && cta.includes('github.com')) {
              githubUrl = cta;
            }

            if (external && !external.includes('github.com')) {
              siteUrl = external;
            } else if (cta && !cta.includes('github.com')) {
              siteUrl = cta;
            } else if (github && !github.includes('github.com')) {
              siteUrl = github;
            }

            return (
              <StyledProjectCard key={i} ref={el => (revealProjects.current[i] = el)}>
                <div className="terminal-header">
                  <div className="terminal-dots">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                  <div className="terminal-title">
                    admin@system:~/projects/{title.toLowerCase().replace(/\s+/g, '_')}
                  </div>
                </div>
                <div className="project-image">
                  <GatsbyImage image={image} alt={title} className="img" />
                </div>

                <div className="project-content">
                  <p className="project-overline">Project</p>
                  <h3 className="project-title" ref={el => (titleRefs.current[i] = el)}>
                    {title}
                  </h3>

                  <div
                    className="project-description"
                    ref={el => (descRefs.current[i] = el)}
                    dangerouslySetInnerHTML={{ __html: cleanHtml }}
                  />

                  {tech.length && (
                    <ul className="project-tech-list">
                      {tech.map((techItem, i) => (
                        <li key={i}>{techItem}</li>
                      ))}
                    </ul>
                  )}

                  <div className="project-links">
                    {githubUrl && (
                      <a
                        href={githubUrl}
                        className="link-btn github-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="GitHub Link"
                      >
                        <Icon name="GitHub" />
                        <span>GitHub</span>
                      </a>
                    )}
                    {siteUrl && (
                      <a
                        href={siteUrl}
                        className="link-btn site-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="External Link"
                      >
                        <Icon name="External" />
                        <span>Live Site</span>
                      </a>
                    )}
                  </div>
                </div>
              </StyledProjectCard>
            );
          })}
      </div>
    </StyledProjectsSection>
  );
};

export default Featured;
